import { Annotations } from '@/components/annotations/annotations';
import { Flex, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './conversation-detail.module.scss';
import { TranscriptMessage } from '@/components/transcript-message/transcript-message';
import { StartEndCall } from '@/components/start-end-call/start-end-call';
import CallRecording from '@/components/call-recording/call-recording';
import { convertToSeconds, downloadAudioFile, durationSeconds } from '@/utils/helpers';
import { useGetTxtFile } from '@/hooks/use-get-txt-file/use-get-txt-file';
import LanguagesSwitch from '@/components/language-switch/language-switch';
import Loader from '@/components/loader/loader';
import { MaintenanceCard } from '@/components/maintenance-card/maintenance-card';
import { useReviewedCall } from '@/hooks/use-reviewed-call/use-reviewed-call';
import { Checkbox } from '@/components/checkbox/checkbox';
import Callout from '@/components/callout/callout';
import SectionLabel from '@/components/section-label/section-label';
import DrawerFacts from './drawer-facts';
import { CallDetail } from '@/services/call-log-service/call-log-service.types';
import { AnnotationPayload } from '@/services/conversations-service/conversations-service.types';

interface ConversationDetailProps {
  call: CallDetail;
  callId: string;
  translate: string;
  onTranslateChange: (value: string) => void;
  isFetching: boolean;
  addAnnotation: (payload: AnnotationPayload) => void;
  isAddingAnnotation: boolean;
}

/**
 * Body of the conversation drawer. Mockup blocks come first (player, AI summary, facts,
 * transcript), then the features the mockup has no counterpart for. None of those are
 * dropped: removing a working feature is a product decision, not a restyle.
 */
export function ConversationDetail({
  call,
  callId,
  translate,
  onTranslateChange,
  isFetching,
  addAnnotation,
  isAddingAnnotation,
}: ConversationDetailProps) {
  const { t } = useTranslation('conversations');
  const [annotation, setAnnotation] = useState('');
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const [isAudioPending, setIsAudioPending] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const { downloadTXT, isPending } = useGetTxtFile(+callId);
  const { checkCall } = useReviewedCall({ analytics_call_id: callId });

  // Fall through: AI clip, then post-claim bridge clip, then legacy agent clip.
  const recordingUrl = call.audio_url || call.bridge_audio_url || call.agent_audio_url;

  const handleDownloadTxt = () => {
    downloadTXT()
      .then(() => {
        messageApi.open({ type: 'success', content: t('drawer.toast.txtDownloaded') });
      })
      .catch(() => {
        messageApi.open({ type: 'error', content: t('drawer.toast.txtFailed') });
      });
  };

  const handleShare = () => {
    // Now the list URL plus /:callId, which is a better deep link than the old page was.
    // clipboard is undefined outside a secure context, so the call itself can throw before
    // there is a promise to reject.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(window.location.href))
      .then(() => {
        messageApi.open({ type: 'success', content: t('drawer.toast.linkCopied') });
      })
      .catch(() => {
        messageApi.open({ type: 'error', content: t('drawer.toast.linkFailed') });
      });
  };

  const handleDownloadAudio = () => {
    if (!recordingUrl) return;
    setIsAudioPending(true);
    fetch(recordingUrl)
      .then((response) => response.blob())
      .then((audioBlob) => {
        downloadAudioFile(audioBlob, 'call-audio');
        messageApi.open({ type: 'success', content: t('drawer.toast.audioDownloaded') });
      })
      .catch(() => {
        messageApi.open({ type: 'error', content: t('drawer.toast.audioFailed') });
      })
      .finally(() => {
        setIsAudioPending(false);
      });
  };

  const isEnglish = call.language?.toLowerCase().startsWith('en');
  const showLanguageSwitch = call.language !== null && !isEnglish;

  useEffect(() => {
    setIsReviewed(Boolean(call.reviewed));
  }, [call.reviewed]);

  return (
    <Flex vertical>
      {contextHolder}

      {recordingUrl ? (
        <CallRecording
          isDownloadingAudio={isAudioPending}
          handleDownloadAudio={handleDownloadAudio}
          handleShare={handleShare}
          handleDownloadAsTxt={handleDownloadTxt}
          isDownloadingTxt={isPending}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          duration={duration}
          setDuration={setDuration}
          status={call.status}
          connectAgent={call.agent}
          src={recordingUrl}
          isLoading={false}
          callDate={new Date(call.created_at)}
          callDuration={durationSeconds(call.duration) ?? 0}
          maintenances={call.maintenance}
          relatedCall={call.related_call}
          callType={call.type}
          waveHeight={30}
          dark
        />
      ) : (
        <Flex vertical>
          <Flex justify="center" className={styles.noRecording}>
            {t('drawer.recording.unavailable')}
          </Flex>
          {/* Guarded, like topics in the table: the type says non-optional, but that is
              an assertion about the payload rather than something we verify. */}
          {call.maintenance?.length ? (
            <Flex className={styles.widgetWrapper}>
              <MaintenanceCard
                className={styles.maintenanceCard}
                maintenances={call.maintenance}
                carouselClassName={styles.carousel}
              />
            </Flex>
          ) : null}
        </Flex>
      )}

      {/* No SectionLabel above this one: Callout's own title is already a micro label, so
          two of them stack up as a doubled heading. */}
      <Callout tone="strong" title={t('drawer.sections.aiSummary')}>
        {call.convo_summary ?? t('drawer.noSummary')}
      </Callout>

      <SectionLabel>{t('drawer.sections.caseData')}</SectionLabel>
      <DrawerFacts call={call} />

      <SectionLabel>{t('drawer.sections.transcript')}</SectionLabel>
      {isFetching ? (
        <Flex vertical align="center" className={styles.recordingFallback}>
          <Loader />
          <p>{t('drawer.translationLoading')}</p>
        </Flex>
      ) : (
        <>
          {showLanguageSwitch && (
            <div className={styles.languageSwitch}>
              <LanguagesSwitch
                active={translate === 'false' ? 'original' : 'english'}
                onSwitch={onTranslateChange}
                flagUrl={call.language_flag_url || ''}
              />
            </div>
          )}
          <StartEndCall type="start" phone={call.phone ?? ''} />
          {!call.transcriptions?.length ? (
            <Flex vertical align="center" justify="center" className={styles.emptyTranscript}>
              <p className={styles.emptyTranscriptText}>{t('drawer.transcriptUnavailable')}</p>
            </Flex>
          ) : (
            call.transcriptions.map(({ id, answer, question, keywords, document_id }) => (
              <div key={id}>
                {question && (
                  <TranscriptMessage field="question" messageText={question} />
                )}
                {answer && (
                  // Keywords and the document belong to the answer: they are what the
                  // assistant matched against to produce it.
                  <TranscriptMessage
                    field="answer"
                    messageText={answer}
                    detectedKeywords={keywords}
                    document_id={document_id}
                  />
                )}
              </div>
            ))
          )}
          <StartEndCall type="end" phone={call.phone ?? ''} />
        </>
      )}

      <SectionLabel>{t('drawer.sections.annotations')}</SectionLabel>
      <Annotations
        annotations={call.annotations || []}
        onTextChange={setAnnotation}
        value={annotation}
        addAnnotation={(value) => {
          addAnnotation({
            message: value,
            start_second: 0,
            end_second: convertToSeconds(currentTime) || convertToSeconds(duration),
          });
          setAnnotation('');
        }}
        isLoading={isAddingAnnotation}
      />

      <Flex gap={10} className={styles.reviewedRow}>
        <p>{t('drawer.markReviewed')}</p>
        <Checkbox
          className={styles.statusCheckbox}
          checked={isReviewed}
          onChange={(e) => {
            const value = e.target.checked;
            setIsReviewed(value);
            checkCall(value);
          }}
        />
      </Flex>

      {call.sms_summary_message && (
        <Flex vertical className={styles.smsSummaryContainer}>
          <SectionLabel>{t('drawer.sections.smsSummary')}</SectionLabel>
          <p>{call.sms_summary_message}</p>
        </Flex>
      )}
    </Flex>
  );
}
